import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ShieldCheck, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { usersAPI } from '../../lib/api';

// Canonical permissions schema. Must mirror backend `PERMISSION_MODULES`.
const MODULES = [
  {
    key: 'kpis',
    label: 'KPIs',
    submenus: [
      { key: 'dashboard', label: 'Dashboard' },
      { key: 'kpis', label: 'KPIs' },
      { key: 'captura', label: 'Captura' },
      { key: 'calendario', label: 'Calendario' },
    ],
  },
  {
    key: 'mesa_de_ayuda',
    label: 'Mesa de Ayuda',
    submenus: [
      { key: 'dashboard', label: 'Dashboard' },
      { key: 'categorias', label: 'Categorías' },
      { key: 'informes', label: 'Informes' },
      { key: 'logs_email', label: 'Logs de Email' },
      { key: 'configuracion', label: 'Configuración' },
    ],
  },
  { key: 'organigrama', label: 'Organigrama', submenus: null },
  { key: 'base_de_conocimiento', label: 'Base de Conocimiento', submenus: null },
  {
    key: 'capital_humano',
    label: 'Capital Humano',
    submenus: [
      { key: 'organigrama', label: 'Organigrama' },
      { key: 'colaboradores', label: 'Colaboradores' },
      { key: 'vacantes', label: 'Vacantes' },
    ],
  },
  {
    key: 'vigilancia',
    label: 'Vigilancia',
    submenus: [
      { key: 'proveedores_visitas', label: 'Proveedores / Visitas' },
      { key: 'herramentales', label: 'Herramentales' },
      { key: 'personal', label: 'Personal' },
    ],
  },
];

const LEVELS = [
  { value: 'none', label: 'Sin acceso' },
  { value: 'basic', label: 'Básico' },
  { value: 'full', label: 'Completo' },
];

const buildDefault = () => {
  const result = {};
  MODULES.forEach(mod => {
    if (!mod.submenus) {
      result[mod.key] = 'none';
    } else {
      result[mod.key] = {};
      mod.submenus.forEach(sub => {
        result[mod.key][sub.key] = 'none';
      });
    }
  });
  return result;
};

// Accent color per level for visual hint
const LEVEL_ACCENT = {
  none: 'text-slate-400',
  basic: 'text-amber-600',
  full: 'text-emerald-600',
};

const RadioRow = ({ value, onChange, name, testidPrefix }) => (
  <div className="flex items-center gap-4 flex-wrap">
    {LEVELS.map(level => {
      const selected = value === level.value;
      return (
        <label
          key={level.value}
          className={cn(
            'flex items-center gap-1.5 cursor-pointer select-none text-sm',
            selected ? LEVEL_ACCENT[level.value] : 'text-slate-500 hover:text-slate-700'
          )}
          data-testid={`${testidPrefix}-${level.value}-label`}
        >
          <input
            type="radio"
            name={name}
            value={level.value}
            checked={selected}
            onChange={() => onChange(level.value)}
            className={cn(
              'w-4 h-4 cursor-pointer',
              level.value === 'none' && 'accent-slate-500',
              level.value === 'basic' && 'accent-amber-500',
              level.value === 'full' && 'accent-emerald-500'
            )}
            data-testid={`${testidPrefix}-${level.value}`}
          />
          <span className={cn(selected && 'font-medium')}>{level.label}</span>
        </label>
      );
    })}
  </div>
);

const ModuleBlock = ({ module, value, onChange, userId }) => {
  const [open, setOpen] = useState(false);
  const isSingle = !module.submenus;
  const summary = useMemo(() => {
    if (isSingle) {
      return LEVELS.find(l => l.value === value)?.label || 'Sin acceso';
    }
    const counts = { none: 0, basic: 0, full: 0 };
    module.submenus.forEach(sub => {
      const v = (value && value[sub.key]) || 'none';
      counts[v] = (counts[v] || 0) + 1;
    });
    return `${counts.full} completo · ${counts.basic} básico · ${counts.none} sin acceso`;
  }, [isSingle, module, value]);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        onClick={() => setOpen(o => !o)}
        data-testid={`perm-module-${module.key}-toggle`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
          )}
          <span className="font-medium text-slate-800 text-sm">{module.label}</span>
        </div>
        <span className="text-xs text-slate-500 truncate ml-2">{summary}</span>
      </button>
      {open && (
        <div className="p-3 space-y-2.5 bg-white">
          {isSingle ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-700">Acceso al módulo</span>
              <RadioRow
                value={value || 'none'}
                onChange={v => onChange(module.key, null, v)}
                name={`perm-${userId}-${module.key}`}
                testidPrefix={`perm-${module.key}`}
              />
            </div>
          ) : (
            module.submenus.map(sub => (
              <div
                key={sub.key}
                className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-b-0"
              >
                <span className="text-sm text-slate-700 min-w-[120px]">{sub.label}</span>
                <RadioRow
                  value={(value && value[sub.key]) || 'none'}
                  onChange={v => onChange(module.key, sub.key, v)}
                  name={`perm-${userId}-${module.key}-${sub.key}`}
                  testidPrefix={`perm-${module.key}-${sub.key}`}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const GranularPermissionsSection = ({ user }) => {
  const eligible = user && (user.role === 'user' || user.role === 'viewer');

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState(null);
  const [current, setCurrent] = useState(null);

  // Fetch permissions when the section is expanded for the first time
  useEffect(() => {
    if (!eligible || !expanded || initial) return;
    let cancelled = false;
    setLoading(true);
    usersAPI
      .getPermissions(user.id)
      .then(res => {
        if (cancelled) return;
        const perms = res.data?.permissions || buildDefault();
        setInitial(perms);
        setCurrent(JSON.parse(JSON.stringify(perms)));
      })
      .catch(() => {
        if (cancelled) return;
        const perms = buildDefault();
        setInitial(perms);
        setCurrent(perms);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eligible, expanded, user?.id, initial]);

  // Reset when user changes
  useEffect(() => {
    setInitial(null);
    setCurrent(null);
    setExpanded(false);
  }, [user?.id]);

  const dirty = useMemo(() => {
    if (!initial || !current) return false;
    return JSON.stringify(initial) !== JSON.stringify(current);
  }, [initial, current]);

  if (!eligible) return null;

  const handleChange = (moduleKey, subKey, value) => {
    setCurrent(prev => {
      const next = { ...prev };
      if (subKey == null) {
        next[moduleKey] = value;
      } else {
        next[moduleKey] = { ...(prev[moduleKey] || {}), [subKey]: value };
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const res = await usersAPI.updatePermissions(user.id, current);
      const saved = res.data?.permissions || current;
      setInitial(JSON.parse(JSON.stringify(saved)));
      setCurrent(JSON.parse(JSON.stringify(saved)));
      toast.success('Permisos guardados correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg" data-testid="granular-permissions-section">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
        onClick={() => setExpanded(e => !e)}
        data-testid="granular-permissions-toggle"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
          <ShieldCheck className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-slate-800 text-sm">Permisos</span>
        </div>
        {dirty && (
          <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            Cambios sin guardar
          </span>
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-slate-100">
          {loading || !current ? (
            <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando permisos...
            </div>
          ) : (
            <>
              {MODULES.map(mod => (
                <ModuleBlock
                  key={mod.key}
                  module={mod}
                  value={current[mod.key]}
                  onChange={handleChange}
                  userId={user.id}
                />
              ))}
              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  data-testid="save-permissions-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar permisos
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GranularPermissionsSection;
